import type { ChatMessage, ProviderName } from "../types";

export interface StreamChatOptions {
  url: string;
  sessionId: string;
  messages: ChatMessage[];
  provider?: ProviderName;
  model?: string;
  maxTokens?: number;
  system?: string;
  signal: AbortSignal;

  onMeta?: (meta: { provider: string; model: string; sessionId: string }) => void;
  onText: (delta: string, arrivedAtMs: number) => void;
  onDone: (meta: { inputTokens?: number; outputTokens?: number }) => void;
  onError: (err: Error) => void;
}

interface SSEFrame {
  event: string;
  data: string;
}

function* parseFrames(buffer: string): Generator<SSEFrame> {
  const lines = buffer.split("\n");
  let event = "message";
  const dataLines: string[] = [];

  const flush = () => {
    if (dataLines.length === 0 && event === "message") return null;
    const frame: SSEFrame = { event, data: dataLines.join("\n") };
    event = "message";
    dataLines.length = 0;
    return frame;
  };

  for (const raw of lines) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (line === "") {
      const frame = flush();
      if (frame) yield frame;
      continue;
    }
    if (line.startsWith(":")) continue; // comment / heartbeat
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^ /, ""));
    }
  }
}

export async function streamChat(opts: StreamChatOptions): Promise<void> {
  const body = JSON.stringify({
    sessionId: opts.sessionId,
    messages: opts.messages,
    provider: opts.provider,
    model: opts.model,
    maxTokens: opts.maxTokens,
    system: opts.system,
  });

  let response: Response;
  try {
    response = await fetch(opts.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body,
      signal: opts.signal,
      credentials: "include",
    });
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    opts.onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    opts.onError(
      new Error(`HTTP ${response.status} ${response.statusText}${text ? `: ${text}` : ""}`)
    );
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let pending = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });

      let separatorIndex: number;
      while ((separatorIndex = pending.indexOf("\n\n")) !== -1) {
        const block = pending.slice(0, separatorIndex);
        pending = pending.slice(separatorIndex + 2);
        for (const frame of parseFrames(block + "\n")) {
          handleFrame(frame, opts);
        }
      }
    }
    if (pending.trim().length > 0) {
      for (const frame of parseFrames(pending + "\n")) {
        handleFrame(frame, opts);
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    opts.onError(err instanceof Error ? err : new Error(String(err)));
  }
}

function handleFrame(frame: SSEFrame, opts: StreamChatOptions): void {
  let parsed: unknown;
  try {
    parsed = frame.data ? JSON.parse(frame.data) : null;
  } catch {
    // Non-JSON payload — skip.
    return;
  }

  switch (frame.event) {
    case "message": {
      const text = (parsed as { text?: string } | null)?.text;
      if (typeof text === "string" && text.length > 0) {
        const arrivedAt = performance.now();
        // eslint-disable-next-line no-console
        console.log(`[sse chunk] +${arrivedAt.toFixed(1)}ms`, JSON.stringify(text));
        opts.onText(text, arrivedAt);
      }
      return;
    }
    case "meta": {
      const meta = parsed as { provider?: string; model?: string; sessionId?: string } | null;
      if (meta && meta.provider && meta.model && meta.sessionId) {
        opts.onMeta?.({
          provider: meta.provider,
          model: meta.model,
          sessionId: meta.sessionId,
        });
      }
      return;
    }
    case "done": {
      const meta = parsed as { inputTokens?: number; outputTokens?: number } | null;
      opts.onDone({
        inputTokens: meta?.inputTokens,
        outputTokens: meta?.outputTokens,
      });
      return;
    }
    case "error": {
      const message =
        (parsed as { error?: string } | null)?.error ?? "Unknown stream error";
      opts.onError(new Error(message));
      return;
    }
    default:
      return;
  }
}

export function approxTokensPerSecond(text: string, durationMs: number): number | null {
  if (durationMs <= 0) return null;
  const tokens = text.split(/\s+/).filter(Boolean).length;
  return tokens / (durationMs / 1000);
}
